/**
 * Contact detail — the person on their own page, outside any list
 * (`/people/in/:handle?tab=…`, flag `person-panel`, behind `?preview=2` while
 * unreleased — `FeatureRoute` sends a visitor without it to /dashboard).
 *
 * The same `PersonView` the drawer renders, in `page` mode: no per-contact
 * actions (they need a list row to act on), and an "Open in a list" card
 * instead, one link per list the person is on.
 */
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { type ImportsClient, createImportsClient } from "../../clients/imports.client";
import { type PeopleClient, defaultPeopleClient } from "../../clients/people.client";
import { CopyLinkPopover } from "../../features/person-panel/CopyLinkPopover";
import { PersonView } from "../../features/person-panel/PersonView";
import {
  type PersonTab,
  isPersonHandle,
  parsePersonTab,
} from "../../features/person-panel/personHandle";
import { personShareUrl } from "../../features/person-panel/sharePreview";
import { usePerson } from "../../features/person-panel/usePerson";
import { personPagePath } from "../../routing/paths";
import { useResolveAvatars } from "../research/import-detail/useResolveAvatars";

export function ContactDetailPage({
  client,
  importsClient,
}: {
  /** Injected in tests; defaults to the env-configured People client. */
  client?: PeopleClient;
  /** Injected in tests; presigns the stored avatar. */
  importsClient?: ImportsClient;
}) {
  const { handle: rawHandle = "" } = useParams<{ handle: string }>();
  const handle = isPersonHandle(rawHandle) ? rawHandle : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parsePersonTab(searchParams.get("tab"));
  const api = useMemo(() => client ?? defaultPeopleClient(), [client]);
  const importsApi = useMemo(
    () => importsClient ?? createImportsClient({ apiBase: import.meta.env.VITE_API_URL ?? "" }),
    [importsClient]
  );

  // Same query key PersonView reads, so this is the cached answer, not a
  // second request — only here to learn the stored avatar key.
  const person = usePerson(api, handle, null);
  const avatarKey = person.data?.identity.profile_image_s3_key;
  const avatarVersion = person.data?.identity.profile_image_captured_at;
  const avatarRows = useMemo(
    () =>
      avatarKey
        ? [{ profile_image_s3_key: avatarKey, profile_image_captured_at: avatarVersion }]
        : [],
    [avatarKey, avatarVersion]
  );
  const avatars = useResolveAvatars(importsApi, avatarRows);

  const setTab = (t: PersonTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", t);
        return next;
      },
      { replace: true }
    );

  return (
    <div className="p-8" data-testid="contact-detail-page">
      <Link
        to="/research/imports"
        className="text-caption text-text-secondary hover:text-accent-ink"
        data-testid="contact-detail-back"
      >
        ← Back to People Lists
      </Link>
      <div className="mt-3 overflow-hidden rounded-lg border border-border bg-bg">
        <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2">
          <h1 className="min-w-0 flex-1 truncate text-h3 text-text">
            Contact detail
            {handle && (
              <span className="font-normal text-text-secondary"> · linkedin.com/in/{handle}</span>
            )}
          </h1>
          {handle && (
            <CopyLinkPopover
              links={[
                {
                  id: "page",
                  label: "Contact detail",
                  description:
                    "The person on their own, whatever list they came from. Keyed by the LinkedIn URL, so it works across lists and searches.",
                  url: personShareUrl(
                    `${personPagePath(handle)}${tab === "profile" ? "" : `?tab=${tab}`}`
                  ),
                },
              ]}
            />
          )}
        </div>
        {handle ? (
          <PersonView
            // One mount per person: Back/Forward between two contact pages must
            // not carry the "No list selected" pick over (/pr-review #954 iter 3).
            key={handle}
            client={api}
            handle={handle}
            importId={null}
            tab={tab}
            onTabChange={setTab}
            mode="page"
            avatarUrl={avatarKey ? avatars.get(avatarKey) : undefined}
          />
        ) : (
          <p className="p-6 text-body text-text-secondary" data-testid="contact-detail-bad-handle">
            That address is not a LinkedIn profile handle.
          </p>
        )}
      </div>
    </div>
  );
}
